import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { getLicenseRule, updateLicenseRule, previewLicenseRule, deleteLicenseRule } from '../../services/licenseService';
import AssignmentTargetPicker, { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import Button from '../../components/ui/Button';
import useLayoutBucket from '../../hooks/useLayoutBucket';
import { drawerGeometry } from '../../components/layout/shellMetrics';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
  flex-shrink: 0;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #004242;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  &:hover { background: #F5F7FA; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
  flex-shrink: 0;
`;

const Summary = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.text?.dark?.little || '#004242'};
  line-height: 1.6;
`;

const BlockWarning = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #E53935;
  background: rgba(229, 57, 53, 0.08);
  border-left: 3px solid #E53935;
  border-radius: 4px;
  padding: 8px 10px;
`;

// Düzeltme turu 1 (koordinatör incelemesi): kendi ad-hoc hedef seçicimiz
// (`{targets:[{type,ids,excluded}]}`) uydurma bir sözleşmeydi — repoda bu iş
// için ZATEN backend'in `AssignmentScopeRequest` DTO'suna (IncludeAll,
// DepartmentIds, LocationIds, UserGroupIds, UserIds, SubTenantIds, TitleIds,
// Excludes, AutoApplyOnEnter) birebir karşılık gelen bir frontend sözleşmesi
// var: `AssignmentTargetPicker`'ın `value`/`onChange` şekli (bkz.
// `AccessScopeEditor.jsx` — GateScopeTab/GateGroupScopeTab onu bu şekilde
// kullanıyor). `AccessScopeEditor`'ın kendisi kullanılamaz çünkü kendi
// içinde GET/PUT yapıp değeri dışarı vermiyor — canlı önizleme (previewLicenseRule)
// için ham değere ihtiyaç var, o yüzden picker doğrudan burada, yükleme/
// kaydetme/önizleme bu bileşende tutuluyor (AccessScopeEditor'a DOKUNULMADI).
// Düzeltme turu 2 (koordinatör incelemesi Bulgu 1): bu çekmece `GenericFormDrawer`
// KULLANAMAZ — canlı önizleme paneli, `AssignmentTargetPicker`'ın kendi
// yerleşimi ve "kaydet" davranışı (Karar 2: bloke uyarısı kaydetmeyi
// engellemez) GenericFormDrawer'ın şema-tabanlı form yapısına (yalnız `t
// (schema)` + `onSave(formData)`, ara durumu dışarı VERMEZ) sığmıyor —
// önizleme her tuş vuruşunda `previewLicenseRule` çağırmalı, GenericFormDrawer
// ise formData'yı yalnız submit anında dışarı verir. Bu yüzden kabuk ham
// `Drawer` kalıyor AMA genişliği GenericFormDrawer'ın KENDİSİYLE AYNI kaynaktan
// (`useLayoutBucket` + `drawerGeometry`) hesaplanıyor — önceki sürümdeki sabit
// `size={420}` 420px'ten dar telefonlarda çekmeceyi ekrandan taşırıyordu
// (GenericFormDrawer'ın FE-08'de zaten çözdüğü hatanın yeniden açılması).
// Kuralın DAHİL hedefi var mı (backend LicenseDataAccess.DahilHedefiVar aynası). Yoksa kaydetmek
// anlamsızdır: API 400 `license.ruleEmpty` döner — kural kaldırmanın yolu "Kuralı Kaldır"dır.
export const hasIncludeTarget = (scope) => {
  if (!scope) return false;
  if (scope.includeAll) return true;
  return ['departmentIds', 'locationIds', 'userGroupIds', 'userIds', 'subTenantIds', 'titleIds']
    .some((alan) => (scope[alan] || []).length > 0);
};

// Önizleme her kapsam değişiminde çağrılır; her tuş/seçim için istek atmamak için geciktirilir
// (P4Kü-9 — "tüm şirket" kuralında sayım ucu SQL'de COUNT(DISTINCT …) koşar, ama yine de art arda
// 10 seçimde 10 istek gitmemeli).
export const PREVIEW_DEBOUNCE_MS = 400;

export default function LicenseRuleDrawer({ open, moduleCode, onClose, onSaved }) {
  const { t } = useTranslation();
  const bucket = useLayoutBucket();
  const geometry = drawerGeometry(bucket);
  const [scope, setScope] = useState(EMPTY_TARGETS);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  // P4Kü-5 — KURAL YÜKLENDİ Mİ? Kaydet, GET dönmeden (ve GET başarısızsa HİÇ) açılmaz: eski davranışta
  // boş kapsamla kaydetmek havuzun TÜM kural koltuklarını iade ediyordu ("kazara toplu iade").
  const [loaded, setLoaded] = useState(false);
  // Yüklenen kuralda dahil hedefi var mıydı — "Kuralı Kaldır" yalnız o zaman anlamlıdır.
  const [hadRule, setHadRule] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Kuralı yükle (AccessScopeEditor'ın getAccessScope sonrası yaptığı AYNI
  // eksiksiz-birleştirme: backend kısmi bir nesne dönerse bile excludes dahil
  // her alan EMPTY_TARGETS varsayılanlarıyla tamamlanır).
  useEffect(() => {
    if (!open || !moduleCode) return;
    setScope(EMPTY_TARGETS);
    setPreview(null);
    setLoaded(false);
    setHadRule(false);
    setConfirmRemove(false);
    getLicenseRule(moduleCode)
      .then((res) => {
        // GET okumaları reject etmez; gerçek başarı sinyali res.data.status'tur.
        if (res.data?.status !== false) {
          const d = res.data?.data || {};
          const yuklenen = {
            ...EMPTY_TARGETS,
            ...d,
            excludes: { ...EMPTY_TARGETS.excludes, ...(d.excludes || {}) },
          };
          setScope(yuklenen);
          setHadRule(hasIncludeTarget(yuklenen));
          setLoaded(true);
        }
      })
      .catch(() => {});
  }, [open, moduleCode]);

  // Kapsam her değiştiğinde önizleme çağrılır — AssignmentScopeRequest şekli
  // AYNEN gönderilir (sarmalama YOK). Kural yüklenmeden çağrılmaz: yükleme yarışında
  // BOŞ kapsamın önizlemesi gösterilirdi.
  useEffect(() => {
    if (!open || !moduleCode || !loaded) return undefined;

    const zamanlayici = setTimeout(() => {
      previewLicenseRule(moduleCode, scope)
        .then((res) => {
          if (res.data?.status !== false) setPreview(res.data?.data || null);
        })
        .catch(() => {});
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(zamanlayici);
  }, [open, moduleCode, scope, loaded]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // KARAR 2: tavan aşılınca işlem iptal edilmez, fazlalar bloke kalır —
      // wouldBlockCount ne olursa olsun kaydet çağrılır, yalnız özet metni değişir.
      await updateLicenseRule(moduleCode, scope);
      onSaved?.();
      onClose?.();
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [moduleCode, scope, onSaved, onClose]);

  // Kuralı kaldırmak havuzun TÜM kural koltuklarını iade eder: İKİ AŞAMALI onay (modal YOK —
  // düğme önce uyarıya, sonra onaya döner; UI altın kuralı).
  const handleRemove = useCallback(async () => {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }

    setRemoving(true);
    try {
      await deleteLicenseRule(moduleCode);
      onSaved?.();
      onClose?.();
    } catch {
      // handled by interceptor
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  }, [confirmRemove, moduleCode, onSaved, onClose]);

  const wouldBlockCount = preview?.wouldBlockCount || 0;
  const kaydedilebilir = loaded && hasIncludeTarget(scope) && !saving && !removing;

  const summaryText = useMemo(() => {
    if (!preview) return null;
    const covered = preview.coveredUserCount ?? 0;
    const newSeats = preview.newSeatCount ?? 0;
    const usageText = preview.seatLimit != null
      ? `${preview.currentUsage ?? 0}/${preview.seatLimit}`
      : t('license.unlimited');
    return `${t('license.rulePreview', { covered, newSeats })} · ${usageText}`;
  }, [preview, t]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={geometry.size}
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        minWidth: geometry.minWidth,
        margin: geometry.margin,
        borderRadius: geometry.borderRadius,
        height: geometry.height,
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{t('license.rule')}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>

      <Body>
        {/* P4Kü-12 — `showAutoApply` KALDIRILDI: moduleSeat kapsamı Ruling 48 gereği HER ZAMAN
            AutoApplyOnEnter = true yazılır (backend bayrağı ezer), yani anahtar etkisizdi ve
            "kapatabilirim" yanılgısı üretiyordu. */}
        <AssignmentTargetPicker
          value={scope}
          onChange={setScope}
        />
      </Body>

      <Footer>
        <div style={{ flex: 1 }}>
          {summaryText && <Summary data-testid="rule-preview">{summaryText}</Summary>}
          {confirmRemove && (
            <BlockWarning data-testid="rule-remove-warning">
              {t('license.removeRuleWarning')}
            </BlockWarning>
          )}
          {wouldBlockCount > 0 && (
            <BlockWarning data-testid="rule-block-warning">
              {t('license.ruleWouldBlock', { count: wouldBlockCount })}
            </BlockWarning>
          )}
        </div>
        {hadRule && (
          <Button
            data-testid="rule-remove"
            variant="outline"
            onClick={handleRemove}
            disabled={removing || saving}
          >
            {confirmRemove ? t('license.confirmRemoveRule') : t('license.removeRule')}
          </Button>
        )}
        <Button
          data-testid="rule-save"
          onClick={handleSave}
          disabled={!kaydedilebilir}
        >
          {saving ? t('common.loading') : t('common.save', { defaultValue: 'Kaydet' })}
        </Button>
      </Footer>
    </Drawer>
  );
}
